/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2023, Zumper
 */

import type { GlobalNavLinks } from '@zcode/bizos'
import type {
  GetState,
  GlobalNavLinkPayload,
  GlobalNavLinkPropertyType,
  State,
} from '@zcode/state-type'
import type { RouteComponentProps } from 'react-router-dom'
import type { Values } from 'redux-actions'
import type { ThunkDispatch } from 'redux-thunk'

import { logger } from '../../logger'
import { selectUrlInAnyRoute } from '../app/selectors'

import { doneFetchingLinks, receiveGlobalNavLinks, startFetchingLinks } from './actions'
import type { globalNavSlice } from './globalNavSlice'
import { selectGlobalNavMeta } from './selectors'

export const GLOBAL_NAV_LINK_LIMIT = 15
export const GLOBAL_NAV_MAX_DISTANCE = 40
export const GLOBAL_NAV_PROPERTY_TYPES: GlobalNavLinkPropertyType[] = ['houses', 'rooms']

type LoadGlobalNavLinkpack = GlobalNavLinkPayload & {
  api: {
    get: (payload: GlobalNavLinkPayload) => Promise<GlobalNavLinks>
  }
  routeProps?: {
    match: Pick<
      RouteComponentProps<{ cityState?: string; hoodName?: string }>['match'],
      'params'
    > | null
  }
}

export const loadGlobalNavLinkpack =
  (props: LoadGlobalNavLinkpack) =>
  async (
    dispatch: ThunkDispatch<
      Pick<State, 'globalNav'>,
      unknown,
      ReturnType<Values<(typeof globalNavSlice)['actions']>>
    >,
    getState: GetState
  ) => {
    const state = getState()
    const { api, routeProps } = props
    const limit = props.limit ?? GLOBAL_NAV_LINK_LIMIT
    const maxDistance = props.maxDistance ?? GLOBAL_NAV_MAX_DISTANCE
    const propertyTypes = props.propertyTypes ?? GLOBAL_NAV_PROPERTY_TYPES
    // selectUrlInAnyRoute works most of the time, except for rent research
    let url = props.url ?? selectUrlInAnyRoute(state)
    // rent research provides cityState and hoodName from the routeProps
    if (!url && routeProps?.match?.params?.cityState) {
      url = routeProps.match.params.cityState
      if (routeProps.match.params.hoodName) {
        url += `/${routeProps.match.params.hoodName}`
      }
    }
    const { currentKey, fetchingKey } = selectGlobalNavMeta(state)
    if (String(url) !== currentKey && String(url) !== fetchingKey) {
      // the API accepts null or undefined url, here I san save the string "undefined" as currentKey as a valid key
      dispatch(startFetchingLinks(String(url)))
      try {
        const response = await api.get({
          limit,
          maxDistance,
          propertyTypes,
          url,
        })
        dispatch(receiveGlobalNavLinks(response))
      } catch (error: unknown) {
        dispatch(doneFetchingLinks())
        if (
          !(
            typeof error === 'object' &&
            error &&
            'message' in error &&
            typeof error.message === 'string' &&
            error.message.includes('Failed to fetch')
          )
        ) {
          logger.error(`error getting global nav linkpacks for URL: ${String(url)}.`, error)
        }
      }
    }
  }
